import React, { useState, useEffect } from "react";
import { fetchBrand } from "@/utils/brandLogo";

interface CompanyLogoProps {
  domain?: string;
  className?: string;
  style?: React.CSSProperties;
  height?: string | number;
}

const CompanyLogo: React.FC<CompanyLogoProps> = ({
  domain,
  className = "",
  style,
  height = 32,  
}) => {
  // Use logo from API if available, otherwise fall back to Brandfetch CDN
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    const loadBrandLogo = async () => {
      if (!domain) {
        if (isMounted) {
          setIsLoading(false);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
        setHasError(false);
        setIsLoaded(false);
      }
      
      // Special case for adobe.com - use local image
      if (domain.toLowerCase() === 'adobe.com') {
        if (isMounted) {
          setImageSrc('/adobe.png');
          setIsLoading(false);
        }
        return;
      }
      
      try {
        const result = await fetchBrand(domain);
        
        if (isMounted) {
          if (result?.logoUrl) {
            setImageSrc(result.logoUrl);
          } else {
            setHasError(true);
          }
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    loadBrandLogo();

    return () => {
      isMounted = false;
    };
  }, [domain]);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setHasError(true);
    setIsLoaded(false);
    e.currentTarget.style.display = 'none';
  };

  if (!domain || (hasError && !imageSrc)) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {isLoading && (
        <div 
          className="animate-pulse bg-gray-200 rounded"
          style={{ width: typeof height === 'number' ? height * 2 : height, height, ...style }}
        />
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={`${domain} Logo`}
          className={`w-auto object-contain ${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 ease-in-out`}
          style={{ 
            height, 
            ...style,
            transition: 'opacity 0.3s ease-in-out'
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
          height={height}
        />
      )}
    </div>
  );
};

export default CompanyLogo;

